import { isCoursesContextPath } from '@common/core-layer/utils/route-path-segments'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { defineNuxtRouteMiddleware, navigateTo } from 'nuxt/app'
import { isSameCoursesRedirectTarget, resolveCoursesQueryRedirect } from '../utils/courses-query-redirect-url'
import { getCanonicalCoursesLessonPath, isLegacyCoursesLibraryPath } from '../utils/legacy-course-url'

/**
 * Courses Route Resolver Middleware
 *
 * Redirects retired courses entry points to their canonical routes:
 *   /courses/library or /courses/library-v2
 *     -> /courses/my-courses
 *
 * It also redirects the legacy courses lesson URL shape to the current one:
 *   .../categories/{moduleId}/posts/{lessonId}
 *     -> .../modules/{moduleId}/lessons/{lessonId}
 *
 * HMI-style `?redirectUrl=` deep links (comment notifications, post-login) are
 * consumed here as well. MFA auth preserves that param on `/courses` but does not
 * otherwise navigate to it; without this rewrite the learner stays on the library.
 *
 * This is a legacy-URL canonicalization: the courses rename only swapped the
 * `categories` -> `modules` and `posts` -> `lessons` segment labels, so we rewrite
 * the path in place and preserve the surrounding context (standalone /courses,
 * communities /learning/courses, and the group-relative GoKollab shape). We use a
 * path-based `navigateTo` rather than a named-route `CPNavigateTo` because the
 * GoKollab lesson page has no named-route template (only a page alias), so name-based
 * resolution cannot reproduce that URL; a 1:1 path rewrite is correct for all shapes.
 *
 * Runs on both server and client, so direct loads, SPA navigations, and loads inside
 * the builder iframe are all redirected in place (query + hash preserved).
 */
export default defineNuxtRouteMiddleware(to => {
  const { CPNavigateTo } = useCPNavigation()

  // Only consume HMI `redirectUrl` on courses routes. Doing it on `/login` would
  // navigate to the lesson before auth can keep the post-login destination.
  if (isCoursesContextPath(to.path)) {
    const queryRedirect = resolveCoursesQueryRedirect(to.query as Record<string, unknown>)
    if (
      queryRedirect &&
      !isSameCoursesRedirectTarget({ path: to.path, query: to.query as Record<string, unknown>, hash: to.hash }, queryRedirect)
    ) {
      return navigateTo(
        { path: queryRedirect.path, query: queryRedirect.query, hash: queryRedirect.hash },
        { replace: true, redirectCode: 302 }
      )
    }
  }

  if (isLegacyCoursesLibraryPath(to.path)) {
    return CPNavigateTo({
      name: 'clientportal-courses-my-courses',
      query: to.query,
      hash: to.hash,
      options: { replace: true, redirectCode: 301 },
    })
  }

  const targetPath = getCanonicalCoursesLessonPath(to.path)
  if (!targetPath) {
    return
  }

  return navigateTo(
    // Preserve query (token, builder, location_id, template_id, is_preview, ...) and hash.
    { path: targetPath, query: to.query, hash: to.hash },
    { replace: true, redirectCode: 301 }
  )
})
